import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, Tooltip, AreaChart, Area,
} from "recharts";
import { Share2, TrendingUp, Users, Heart, Video, Sparkles } from "lucide-react";

/**
 * AnalyticsPage — real-data-driven for connected TikTok accounts.
 * Falls back to a clean empty state until at least one social account is connected.
 */
export default function AnalyticsPage() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tiktok, setTiktok] = useState(null); // {connected, account}
  const [history, setHistory] = useState([]); // [{date, followers, likes, videos, engagement}]

  const t = (ar, en) => (lang === "ar" ? ar : en);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [status, hist] = await Promise.all([
          api.get("/tiktok/status").catch(() => ({ data: { connected: false } })),
          api.get("/tiktok/stats/history?days=30").catch(() => ({ data: { series: [] } })),
        ]);
        setTiktok(status.data);
        setHistory(hist.data?.series || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <AppShell>
        <div className="p-12 text-center text-muted-foreground">
          {t("جاري التحميل...", "Loading…")}
        </div>
      </AppShell>
    );
  }

  const hasReal = tiktok?.connected && history.length > 0;

  return (
    <AppShell>
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl">{t("التحليلات", "Analytics")}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t(
              "رؤى حقيقية من حساباتك الاجتماعية المرتبطة.",
              "Real insights from your connected social accounts."
            )}
          </p>
        </div>
        {tiktok?.connected && (
          <div className="text-xs text-muted-foreground" data-testid="analytics-tiktok-last-sync">
            {t("آخر مزامنة:", "Last sync:")}{" "}
            <span className="tabular-nums">
              {tiktok.account?.last_synced_at
                ? new Date(tiktok.account.last_synced_at).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")
                : t("لم تتم بعد", "not yet")}
            </span>
          </div>
        )}
      </div>

      {tiktok?.connected && tiktok?.account?.needs_reconnect && (
        <div data-testid="analytics-tiktok-reconnect-banner"
             className="mb-6 p-4 rounded-sm border border-destructive/40 bg-destructive/5 text-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <strong>{t("انتهت صلاحية ربط TikTok", "TikTok connection expired")}</strong>
            <div className="text-xs text-muted-foreground mt-1">
              {t(
                "لا يمكننا تحديث بياناتك تلقائياً. أعد الربط لاستمرار المزامنة والنشر.",
                "We can't auto-refresh your stats. Reconnect to resume syncing and publishing."
              )}
            </div>
          </div>
          <button
            data-testid="analytics-tiktok-reconnect-btn"
            onClick={() => nav("/app/social")}
            className="nx-btn-accent shrink-0"
          >
            {t("إعادة ربط TikTok", "Reconnect TikTok")}
          </button>
        </div>
      )}

      {hasReal ? <RealAnalytics history={history} account={tiktok.account} lang={lang} /> : <EmptyState lang={lang} nav={nav} />}
    </AppShell>
  );
}

/* ----------------------------- Real charts ---------------------------- */
function RealAnalytics({ history, account, lang }) {
  const t = (ar, en) => (lang === "ar" ? ar : en);
  const latest = history[history.length - 1] || {};
  const first = history[0] || {};
  const dFollowers = (latest.followers || 0) - (first.followers || 0);
  const dLikes = (latest.likes || 0) - (first.likes || 0);
  const dVideos = (latest.videos || 0) - (first.videos || 0);

  const kpis = [
    { key: "followers", label: t("المتابعون", "Followers"), value: latest.followers, delta: dFollowers, icon: Users },
    { key: "likes", label: t("الإعجابات", "Likes"), value: latest.likes, delta: dLikes, icon: Heart },
    { key: "videos", label: t("الفيديوهات", "Videos"), value: latest.videos, delta: dVideos, icon: Video },
    { key: "engagement", label: t("التفاعل %", "Engagement %"), value: latest.engagement, delta: null, icon: Sparkles },
  ];

  return (
    <div data-testid="analytics-real" className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="analytics-kpis">
        {kpis.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.key} data-testid={`analytics-kpi-${k.key}`} className="nx-card p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{k.label}</div>
                <Icon className="w-4 h-4 text-primary" />
              </div>
              <div className="font-display text-3xl tabular-nums">{(k.value ?? 0).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB")}</div>
              {k.delta !== null && (
                <div className={`text-xs mt-1 tabular-nums ${k.delta > 0 ? "text-emerald-600" : k.delta < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                  {k.delta > 0 ? "▲" : k.delta < 0 ? "▼" : "•"} {Math.abs(k.delta).toLocaleString()} {t("خلال ٣٠ يومًا", "in 30 days")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Followers over time */}
      <div className="nx-card p-5" data-testid="analytics-chart-followers">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
          {t("نمو المتابعين — آخر ٣٠ يومًا", "Follower growth — last 30 days")}
        </div>
        <div className="h-64">
          <ResponsiveContainer>
            <AreaChart data={history} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="grad-followers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#B8875D" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#B8875D" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
              <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
              <Area type="monotone" dataKey="followers" stroke="#B8875D" strokeWidth={2} fill="url(#grad-followers)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Engagement + Likes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="nx-card p-5" data-testid="analytics-chart-engagement">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            {t("التفاعل %", "Engagement %")}
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={history}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="engagement" stroke="#002FA7" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="nx-card p-5" data-testid="analytics-chart-likes">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
            {t("الإعجابات التراكمية", "Cumulative likes")}
          </div>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={history}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--background))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="likes" fill="#B8875D" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground text-center" data-testid="analytics-source-note">
        {t(
          "المصدر: TikTok · تُحدَّث البيانات تلقائيًا كل ٦ ساعات.",
          "Source: TikTok · Data auto-refreshes every 6 hours."
        )}
      </div>
    </div>
  );
}

/* ----------------------------- Empty state ---------------------------- */
function EmptyState({ lang, nav }) {
  const t = (ar, en) => (lang === "ar" ? ar : en);
  const chartTitles = {
    followers: t("المتابعون", "Followers"),
    engagement: t("التفاعل الأسبوعي", "Weekly engagement"),
  };
  return (
    <div data-testid="analytics-empty-state" className="nx-card p-8 md:p-12 max-w-4xl mx-auto animate-fade-in-up">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-sm bg-primary/5 text-primary flex items-center justify-center mx-auto mb-5">
          <Share2 className="w-7 h-7" />
        </div>
        <h2 data-testid="analytics-empty-headline" className="font-display text-2xl md:text-3xl mb-3">
          {t("لا توجد بيانات بعد", "No data yet")}
        </h2>
        <p className="text-muted-foreground max-w-lg mx-auto leading-relaxed">
          {t(
            "لن نعرض أرقامًا تجريبية. اربط أول حساب اجتماعي لتظهر تحليلاتك الحقيقية هنا.",
            "We won't show demo numbers. Connect a social account and your real analytics will appear here."
          )}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        {[["empty-analytics-chart-followers", chartTitles.followers, LineChart, Line],
          ["empty-analytics-chart-engagement", chartTitles.engagement, BarChart, Bar]].map(([tid, title, Chart, Series]) => (
          <div key={tid} data-testid={tid} className="p-4 border border-dashed border-border/70 rounded-sm">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{title}</div>
            <div className="h-36 relative">
              <ResponsiveContainer>
                <Chart data={[]}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis dataKey="x" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                  <Series dataKey="value" stroke="#002FA7" fill="none" />
                </Chart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="text-xs uppercase tracking-widest text-muted-foreground/70">
                  {t("لا توجد بيانات", "No data")}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-center">
        <button
          data-testid="analytics-connect-cta"
          onClick={() => nav("/app/social")}
          className="nx-btn-accent inline-flex items-center gap-2"
        >
          <Share2 className="w-4 h-4" /> {t("اربط حساباتك الآن", "Connect a social account")}
        </button>
      </div>
    </div>
  );
}
